/**
 * Workflow Studio — Done tab (W1-A2 / #44605).
 *
 * Port of docs/design/prototypes/workflow-detail/_done-tab.tsx. The prototype
 * was a Next client island fed by a server component; this is a Vite client
 * component fed by `useApi(workflowDone.get)`. It must never value-import
 * workflow-done-live.ts (server-only) — that 500s every route in the shell.
 *
 * Product path: `isFallback` is always false; no frozen exemplar deck.
 */

import * as React from "react";
import {
  Button,
  DataTable,
  Hint,
  IntentBadge,
  MiniStatStrip,
  SectionCard,
  cn,
  formatDateTime,
  type DataTableColumn,
} from "@excellent/ui";
import { ArrowRight, Ban, Check, GitCommit, RotateCcw, X } from "@excellent/ui/icons";
import type {
  CriterionDto as CriterionFixture,
  DependencyGateDto as DependencyGateView,
  DoneTaskDto as DoneTaskView,
  LoopNodeDto as LoopNodeFixture,
  LoopStage,
  ReceiptDto as ReceiptFixture,
  VerificationDto as VerificationFixture,
  WorkflowDonePayload,
} from "@excellent/ui/api/contracts/workflows/done";

/**
 * Props, all plain and serializable — the tab is a client component and the read
 * happens in `page.tsx`. Nothing here imports `_done-live.ts`; that module is
 * `server-only` and pulling it across the boundary 500s the whole shell.
 */
export type DoneTabProps = Pick<
  WorkflowDonePayload,
  | "task"
  | "readable"
  | "unreadableReason"
  | "candidates"
  | "deltaReceipts"
  | "stampTotals"
  | "isFallback"
> & {
  /** Workflow slug — used so the task picker stays on this studio page. */
  slug: string;
  /** Navigate to another ?done= selection (route search update). */
  onSelectTask: (taskId: number) => void;
};

/**
 * The Done tab — the recursive-done loop rendered as a workflow run.
 *
 * The question this tab exists to answer is the one a person actually arrives
 * with: **"is this task actually done, and how do I know?"** Everything here is
 * a different way of answering it, and nothing here is a summary of a number
 * that lives somewhere else.
 *
 *   What happened, in order?        → the spine
 *   What was it measured against?   → the criteria panel
 *   Who said yes, and on what?      → the verification trail
 *   What did finishing unblock?     → the dependency gate
 *
 * ── THE ONE RULE THIS TAB IS BUILT AROUND ───────────────────────────────────
 * The store keeps machine strings — `dod-unmet`, and proofs like
 *
 *   "'pnpm check-emit-coverage' passed on tree 17fffd64143c but the tree under
 *    review is cd6630865f63 — re-run it on the current tree"
 *
 * The page's job is to TRANSLATE those, and the translation goes ABOVE the raw
 * string, never INSTEAD of it. A plain-English headline tells you what class of
 * thing went wrong; the verbatim proof underneath is what you act on, paste into
 * a bug, or check against the ledger. Hiding it would make this a dashboard you
 * cannot debug from — which is precisely the failure mode the fixture doctrine
 * in `_done-fixture.ts` exists to prevent.
 *
 * ── WHY THE SPINE IS NOT A PROGRESS BAR ─────────────────────────────────────
 * The obvious design is nine pills, left to right, filling green. It is wrong,
 * and #44115 is why: it was refused five times across two trees, LANDED, and was
 * then refused a sixth time — after landing — because a verifier minted receipts
 * on main's tip instead of the tree under review. A left-to-right bar cannot
 * draw that, so it would quietly teach everyone that refusal is rare and
 * terminal. It is neither. The spine is a vertical log with a stage rail, and
 * refusals are first-class nodes that show their whole reasoning.
 */

// ── stage vocabulary ─────────────────────────────────────────────────────────

/**
 * All nine stages, in canonical order — including the two (`receipts`,
 * `approve`) that have no ops row and are proven by a table instead: receipts by
 * `gate_run_receipts`, approve by `tasks.verified_by` + the description trail.
 * They are in the rail anyway, because a spine that silently omits the stages
 * without an event stream would imply the loop has seven steps.
 */
const STAGE_ORDER: LoopStage[] = [
  "derive",
  "evaluate",
  "refuse",
  "park",
  "retry",
  "land",
  "promote",
  "integrated",
];

const STAGE_LABEL: Record<LoopStage, string> = {
  derive: "Derive",
  evaluate: "Evaluate",
  refuse: "Refuse",
  park: "Park",
  retry: "Retry",
  land: "Land",
  promote: "Promote",
  integrated: "Integrated",
};

/** What each stage PROVES — the one line that makes the rail self-explaining. */
const STAGE_PROVES: Record<LoopStage, string> = {
  derive: "the task left planning with a Done that can actually be checked",
  evaluate: "every bound criterion was tested against the tree under review",
  refuse: "the transition was denied for one named, machine-readable cause",
  park: "the retry budget ran out and a human was asked for, by name",
  retry: "the failure routed back into work instead of into a person's inbox",
  land: "the task's own commits are present on the branch it targets",
  promote: "the tier's work actually reached main",
  integrated: "done implies an ancestor of main — the cure for stranded-done",
};

/** Refusals and parks read as bad; everything else reads by its own `ok`. */
function nodeIntent(node: LoopNodeFixture) {
  if (node.stage === "park") return "warning" as const;
  return node.ok ? ("success" as const) : ("danger" as const);
}

// ── the spine ────────────────────────────────────────────────────────────────

/**
 * The stage rail. Not a progress bar — a TALLY. `Refuse ×3` is the most useful
 * number on this tab, and a bar cannot show a count greater than one.
 */
function StageRail({ task }: { task: DoneTaskView }) {
  const counts = React.useMemo(() => {
    const c = {} as Record<LoopStage, number>;
    for (const n of task.loop) c[n.stage] = (c[n.stage] ?? 0) + 1;
    return c;
  }, [task]);

  return (
    <div className="flex flex-wrap items-center gap-x-1 gap-y-2">
      {STAGE_ORDER.map((stage, i) => {
        const n = counts[stage] ?? 0;
        const visited = n > 0;
        const repeated = n > 1;
        return (
          <React.Fragment key={stage}>
            {i > 0 && (
              <ArrowRight
                aria-hidden
                className="size-3 shrink-0 text-muted-foreground/40"
              />
            )}
            <span
              title={STAGE_PROVES[stage]}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 type-subtext",
                visited
                  ? repeated
                    ? "bg-destructive/10 text-destructive"
                    : "bg-muted text-foreground"
                  : "text-muted-foreground/50",
              )}
            >
              {STAGE_LABEL[stage]}
              {repeated && <span className="tabular-nums font-medium">×{n}</span>}
            </span>
          </React.Fragment>
        );
      })}
    </div>
  );
}

/**
 * A verbatim machine string. Selectable, wrapping, monospaced — deliberately not
 * truncated, because a proof you have to hover to finish reading is a proof you
 * will not read. `bg-muted` and no shadow: this is a quotation inside a card, not
 * a card of its own.
 */
function RawString({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-md bg-muted px-2.5 py-1.5 font-mono type-subtext leading-body break-words whitespace-pre-wrap text-muted-foreground">
      {children}
    </p>
  );
}

/**
 * The prescribed remedy. The ledger stores a runnable command in `detail.how` /
 * `detail.remedy` / `detail.unpark`, and the entire value of that design is that
 * you can select it and run it — so it renders as a command, at full contrast,
 * not as prose about a command.
 */
function Remedy({ command }: { command: string }) {
  return (
    <div className="flex items-start gap-2">
      <span className="mt-1.5 select-none font-mono type-subtext text-success">$</span>
      <p className="font-mono type-subtext leading-body break-words whitespace-pre-wrap text-foreground">
        {command}
      </p>
    </div>
  );
}

/**
 * One node. The headline is OURS (translated); everything under it is the
 * ledger's own words.
 */
function LoopNodeRow({ node, last }: { node: LoopNodeFixture; last: boolean }) {
  const intent = nodeIntent(node);
  const Glyph = node.stage === "retry" ? RotateCcw : node.ok ? Check : node.stage === "park" ? Ban : X;

  return (
    <li className="flex gap-3">
      {/* Rail: dot + connector. The connector is what makes a list read as a
          sequence; it stops at the last node so the spine has an end. */}
      <div className="flex w-4 shrink-0 flex-col items-center">
        <span
          aria-hidden
          className={cn(
            "flex size-4 items-center justify-center rounded-full",
            intent === "success" && "bg-success/15 text-success",
            intent === "danger" && "bg-destructive/15 text-destructive",
            intent === "warning" && "bg-warning/15 text-warning",
          )}
        >
          <Glyph className="size-2.5" />
        </span>
        {!last && <span aria-hidden className="mt-1 w-px flex-1 bg-border" />}
      </div>

      <div className={cn("flex min-w-0 flex-1 flex-col gap-2", last ? "pb-0" : "pb-5")}>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="type-body font-medium text-foreground">
            {STAGE_LABEL[node.stage]}
          </span>
          {node.attempt !== null && (
            <span className="type-subtext tabular-nums text-muted-foreground">
              attempt {node.attempt}
            </span>
          )}
          {node.tree && (
            <span className="font-mono type-subtext text-muted-foreground">
              tree {node.tree}
            </span>
          )}
          {node.reason && <IntentBadge intent="danger">{node.reason}</IntentBadge>}
          {/* An ops row still `open` is live state, not history. Say so. */}
          {node.ops_status === "open" && (
            <IntentBadge intent="warning" withDot>
              still open
            </IntentBadge>
          )}
          <span className="ml-auto shrink-0 type-subtext text-muted-foreground">
            {formatDateTime(node.at)}
          </span>
        </div>

        {/* The ledger's own title, verbatim. */}
        <p className="type-body leading-ui text-muted-foreground">{node.title}</p>

        {node.delta && (
          <RetryTally
            fixed={node.delta.fixed}
            still={node.delta.still_failing}
            fresh={node.delta.newly_failing}
          />
        )}

        {node.proofs.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <span className="type-subtext font-medium text-foreground">
              {node.ok ? "Proof" : "Why it was refused"}
              <span className="ml-1.5 font-normal text-muted-foreground">
                — verbatim, as the router recorded it
              </span>
            </span>
            {node.proofs.map((p) => (
              <RawString key={p}>{p}</RawString>
            ))}
          </div>
        )}

        {node.remedy && (
          <div className="flex flex-col gap-1.5">
            <span className="type-subtext font-medium text-foreground">Prescribed remedy</span>
            <Remedy command={node.remedy} />
          </div>
        )}

        {node.resolve_note && node.resolve_note !== "decision-record" && (
          <p className="type-subtext text-success">Resolved: {node.resolve_note}</p>
        )}

        {node.ops_kind && (
          <p className="font-mono type-subtext text-muted-foreground/70">
            ops {node.ops_id ?? "—"} · {node.ops_kind}
          </p>
        )}
      </div>
    </li>
  );
}

/**
 * A retry's three tallies. The one that matters is `newly_failing` — criteria
 * that PASSED last time and do not now. That is almost never the work breaking;
 * it is the tree moving underneath receipts that were already green, and it is
 * the single most misread event in the loop.
 */
function RetryTally({
  fixed,
  still,
  fresh,
}: {
  fixed: string[];
  still: string[];
  fresh: string[];
}) {
  const groups: { label: string; items: string[]; className: string }[] = [
    { label: "fixed", items: fixed, className: "text-success" },
    { label: "still failing", items: still, className: "text-destructive" },
    { label: "newly failing", items: fresh, className: "text-destructive" },
  ];
  return (
    <div className="flex flex-col gap-1">
      {groups
        .filter((g) => g.items.length > 0)
        .map((g) => (
          <div key={g.label} className="flex flex-wrap items-baseline gap-x-2 type-subtext">
            <span className={cn("font-medium tabular-nums", g.className)}>
              {g.items.length} {g.label}
            </span>
            <span className="min-w-0 font-mono text-muted-foreground">
              {g.items.join(" · ")}
            </span>
          </div>
        ))}
    </div>
  );
}

function LoopSpine({ task }: { task: DoneTaskView }) {
  return (
    <SectionCard
      title="The loop"
      description="Every node the router recorded for this task, in order. Refusals are not error states here — they are the mechanism, and this one visited them repeatedly."
      aside={<IntentBadge intent="neutral">{task.loop.length} nodes</IntentBadge>}
    >
      <StageRail task={task} />
      <ol className="flex flex-col">
        {task.loop.map((node, i) => (
          <LoopNodeRow
            key={`${node.stage}-${node.at}-${node.ops_id ?? i}`}
            node={node}
            last={i === task.loop.length - 1}
          />
        ))}
      </ol>
    </SectionCard>
  );
}

// ── criteria + receipts ──────────────────────────────────────────────────────

/**
 * How a receipt reads at a glance. Three states, not two, because "green but
 * does not count" is a real and common state — a receipt that exited 0 on a tree
 * that is no longer under review. Collapsing it into "pass" is the bug that cost
 * #44115 two hours.
 */
function receiptState(
  receipt: ReceiptFixture | null,
  binds: boolean,
): { intent: "success" | "danger" | "warning" | "neutral"; label: string } {
  if (!receipt) return { intent: "neutral", label: "no receipt" };
  if (receipt.exit_code !== 0)
    return { intent: "danger", label: `exit ${receipt.exit_code}` };
  if (!binds) return { intent: "warning", label: "green, wrong tree" };
  return { intent: "success", label: "binds" };
}

function CriteriaPanel({ task }: { task: DoneTaskView }) {
  const columns: DataTableColumn<CriterionFixture>[] = [
    {
      key: "ref",
      header: "Criterion",
      render: (c) => (
        <div className="flex min-w-0 flex-col gap-1 py-1">
          <span className="font-mono type-subtext text-foreground">{c.ref}</span>
          <p className="max-w-xl type-subtext leading-ui text-muted-foreground">{c.why}</p>
          <span className="font-mono type-subtext text-muted-foreground/70">
            dod_criteria {c.id} · {c.uid} · {c.source} · {c.kind}
          </span>
        </div>
      ),
    },
    {
      key: "state",
      header: "Receipt",
      width: "w-40",
      render: (c) => {
        const s = receiptState(c.latest, c.binds);
        return (
          <div className="flex flex-col items-start gap-1">
            <IntentBadge intent={s.intent}>{s.label}</IntentBadge>
            {c.latest && (
              <span className="font-mono type-subtext text-muted-foreground/70">
                receipt {c.latest.id} · {c.latest.binding}
              </span>
            )}
          </div>
        );
      },
    },
    {
      key: "tree",
      header: "Bound to",
      width: "w-52",
      render: (c) =>
        c.latest ? (
          <div className="flex flex-col gap-1">
            <span className="font-mono type-subtext text-foreground">
              {c.latest.tree_hash.slice(0, 12)}
            </span>
            {/* Delta receipts name a baseline; absolute ones do not. */}
            {c.latest.mode === "delta" && c.latest.base_tree_hash && (
              <span className="font-mono type-subtext text-muted-foreground">
                base {c.latest.base_tree_hash.slice(0, 12)}
              </span>
            )}
            <span className="type-subtext text-muted-foreground">
              {c.latest.mode ?? "absolute (pre-v157)"}
            </span>
          </div>
        ) : (
          <span className="type-subtext text-muted-foreground">—</span>
        ),
    },
    {
      key: "recorded",
      header: "Recorded",
      width: "w-44",
      render: (c) =>
        c.latest ? (
          <div className="flex flex-col items-start gap-1">
            <span className="type-subtext text-foreground">
              {formatDateTime(c.latest.recorded_at)}
            </span>
            <span className="type-subtext text-muted-foreground">
              untracked{" "}
              {c.latest.untracked_paths === null
                ? "not measured"
                : c.latest.untracked_paths}
            </span>
          </div>
        ) : (
          <span className="type-subtext text-muted-foreground">—</span>
        ),
    },
  ];

  const bound = task.criteria.filter((c) => c.binds).length;

  return (
    <SectionCard
      title="Criteria"
      description="Each derived criterion and the newest gate receipt that answers it. A receipt only counts when it exited 0, ran against a staged tree, and that tree is the one under review — all three, every time."
      aside={
        <IntentBadge intent={bound === task.criteria.length ? "success" : "warning"}>
          {bound}/{task.criteria.length} binding
        </IntentBadge>
      }
    >
      <DataTable
        rows={task.criteria}
        columns={columns}
        rowKey={(c) => c.id}
        wrapInCard={false}
      />
      <MiniStatStrip
        stats={[
          { label: "Tree under review", value: task.tree_under_review, sub: "12-char, as the router prints it" },
          { label: "Criteria bound at", value: formatDateTime(task.criteria[0]?.bound_at), sub: "all derived, none authored" },
          { label: "Refusals before green", value: task.loop.filter((n) => n.stage === "refuse").length, sub: "each one named a cause" },
        ]}
      />
    </SectionCard>
  );
}

// ── verification trail ───────────────────────────────────────────────────────

function VerificationCard({ v }: { v: VerificationFixture }) {
  // Three distinct states (#44394): reject must not paint as silence.
  const intent =
    v.decision === "approve" ? "success" : v.decision === "reject" ? "danger" : "neutral";
  const label =
    v.decision === "approve" ? "approve" : v.decision === "reject" ? "reject" : "no verdict";
  return (
    <li className="flex flex-col gap-2 border-b border-border pb-4 last:border-b-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="font-mono type-body text-foreground">{v.agent_id}</span>
        <IntentBadge intent={intent}>{label}</IntentBadge>
        {v.at && (
          <span className="ml-auto shrink-0 type-subtext text-muted-foreground">
            {formatDateTime(v.at)}
          </span>
        )}
      </div>
      <p className="type-body leading-ui text-muted-foreground">{v.note}</p>
      {v.mutations && (
        <div className="flex flex-col gap-1.5">
          <span className="type-subtext font-medium text-foreground">Mutation battery</span>
          <RawString>{v.mutations}</RawString>
        </div>
      )}
    </li>
  );
}

function VerificationTrail({ task }: { task: DoneTaskView }) {
  const ids = Array.from(new Set(task.verifications.map((v) => v.agent_id)));
  return (
    <SectionCard
      title="Verification"
      description="Who looked, what they ran, and what they concluded. The gate enforces one thing mechanically — the id that approves cannot be the id that claimed — and everything else is evidence a person has to be able to read."
      aside={
        <IntentBadge intent="success">
          {task.shipper} → {task.verifier}
        </IntentBadge>
      }
    >
      <ul className="flex flex-col gap-4">
        {task.verifications.map((v, i) => (
          <VerificationCard key={`${v.agent_id}-${v.at ?? i}`} v={v} />
        ))}
      </ul>
      {/* A verifier who reviews, finds the work sound, and declines to rule is
          not a missing verdict — it is the correct outcome when approving would
          cause a guaranteed mechanical bounce. Both exemplars have one. */}
      <Hint>
        {ids.length === 1
          ? `Both entries are ${ids[0]} — the same verifier, twice: once declining to rule, once approving after the blocker cleared. "Reviewed and declined" is a real outcome, not a gap in the record.`
          : `${ids.length} distinct verifier ids. None of them is the shipper (${task.shipper}) — the MCP gate refuses that with reason 'self-verification-blocked'.`}
      </Hint>
    </SectionCard>
  );
}

// ── landing, twins, and the terminal stamp ───────────────────────────────────

/**
 * The cherry-pick twin. When work lands on the integration tier it becomes a NEW
 * commit — new sha, new parent — and `rewritten_from_sha` is the edge back to
 * the one the shipper actually made. Without drawing that edge, `integration_sha`
 * names a commit nobody in the trail ever wrote, and the whole ledger looks
 * forged. So the twins render as pairs, not as a flat list.
 */
function LandingCard({
  task,
  totals,
}: {
  task: DoneTaskView;
  totals: DoneTabProps["stampTotals"];
}) {
  const twins = task.commits.filter((c) => c.rewritten_from_sha);
  const stamp = task.stamp;

  return (
    <SectionCard
      title="Landing & integration"
      description="Each commit the shipper made, paired with the twin it became on the tier. The terminal state is the stamp: done has to imply an ancestor of main, or the task is stranded."
      aside={<IntentBadge intent="success">{stamp.state}</IntentBadge>}
    >
      <ol className="flex flex-col gap-3">
        {twins.map((twin) => {
          const origin = task.commits.find((c) => c.sha === twin.rewritten_from_sha);
          return (
            <li key={twin.sha} className="flex flex-col gap-2 rounded-md bg-muted/40 p-3">
              <p className="type-body leading-ui text-foreground">{twin.subject}</p>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono type-subtext">
                <GitCommit aria-hidden className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="text-muted-foreground">{origin?.sha.slice(0, 9) ?? "—"}</span>
                <ArrowRight aria-hidden className="size-3 shrink-0 text-muted-foreground/50" />
                <span className="text-foreground">{twin.sha.slice(0, 9)}</span>
                <span className="text-muted-foreground/70">
                  parent {twin.parent_sha.slice(0, 9)}
                </span>
                {twin.sha === stamp.integration_sha && (
                  <IntentBadge intent="success">integration_sha</IntentBadge>
                )}
              </div>
              <span className="font-mono type-subtext text-muted-foreground/70">
                rewritten_from_sha = {twin.rewritten_from_sha} · linked{" "}
                {formatDateTime(twin.linked_at)} · {twin.link_kind}
              </span>
            </li>
          );
        })}
      </ol>

      <MiniStatStrip
        stats={[
          { label: "integration_state", value: stamp.state, sub: `${totals.integrated} integrated · ${totals.noneRequired} none-required` },
          {
            label: "integration_sha",
            value: stamp.integration_sha ? stamp.integration_sha.slice(0, 12) : "none",
            sub: stamp.integration_sha ? "tip of the branch it landed on" : "no attributable commits",
          },
          { label: "integrated_at", value: formatDateTime(stamp.integrated_at), sub: "the timestamp a dependent's claim reads" },
        ]}
      />

      {/* The other terminal state, shown rather than described — a page that only
          ever renders `integrated` will print an em-dash where a sha should be
          the first time it meets one of the vault's none-required tasks. */}
      <Hint>
        Not every done task gets a sha — <strong>{totals.noneRequired}</strong> of
        the {totals.integrated + totals.noneRequired} stamped tasks in this vault
        closed <code>none-required</code>: no commit of their own to land, so{" "}
        <code>integration_sha</code> and <code>integrated_at</code> are both NULL.
        That is the honest answer, not a missing value — but it is{" "}
        <strong>two different ledgers</strong>, and the state does not separate
        them: either no linked commits at all (an ops or docs task), or links
        that are <strong>all citations</strong> of the task rather than
        attributions of its work (#44843). The second kind holds real ledger rows
        that stay there, so do not read this state as an empty ledger; the close
        itself reports which case it met, as{" "}
        <code>integration.citationOnlyLinks</code> on the approve /{" "}
        <code>task_update</code> result. Either way a task stamped{" "}
        <code>none-required</code> can never satisfy a dependent&apos;s claim,
        because the predicate reads the timestamp.
      </Hint>
    </SectionCard>
  );
}

// ── dependency gate ──────────────────────────────────────────────────────────

/**
 * What finishing actually unblocked. This is the only panel that answers "why do
 * I care whether the stamp is right" — because a dependent cannot even be
 * CLAIMED until its blocker is done AND integrated, and the check reads
 * `integrated_at`, the timestamp, not `integration_state`.
 */
function DependencyGate({ gate, taskId }: { gate: DependencyGateView; taskId: number }) {
  return (
    <SectionCard
      title="Dependency gate"
      description="A dependent's claim is refused until its blocker is done AND integrated. The gate fires on the claim, not on done — so nobody burns a lane building on a base that is not on a branch yet."
      aside={<IntentBadge intent="neutral">{gate.edges.length} edge(s)</IntentBadge>}
    >
      <ul className="flex flex-col gap-3">
        {gate.edges.map((e, i) => {
          // Which side is THIS task? An edge reads differently depending on
          // whether you are the thing waiting or the thing being waited on.
          const iAmBlocker = e.blocker?.id === taskId;
          return (
            <li
              key={`${e.blocker?.id ?? "?"}-${e.dependent?.id ?? "?"}-${i}`}
              className="flex flex-col gap-2 rounded-md bg-muted/40 p-3"
            >
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 type-body">
                <span className="font-mono type-subtext text-muted-foreground">
                  #{e.blocker?.id ?? "?"}
                </span>
                <span className="min-w-0 text-foreground">
                  {e.blocker?.title ?? "unknown task"}
                </span>
                <ArrowRight aria-hidden className="size-3.5 shrink-0 text-muted-foreground/50" />
                <span className="font-mono type-subtext text-muted-foreground">
                  #{e.dependent?.id ?? "?"}
                </span>
                <span className="min-w-0 text-foreground">
                  {e.dependent?.title ?? "unknown task"}
                </span>
                <IntentBadge intent={e.satisfied ? "success" : "danger"}>
                  {e.satisfied_by === "cancelled"
                    ? "satisfied — cancelled"
                    : e.satisfied_by === "commitless"
                      ? "satisfied — done, nothing to land"
                      : e.satisfied
                        ? "satisfied"
                        : "blocks the claim"}
                </IntentBadge>
              </div>
              <span className="type-subtext text-muted-foreground">
                {e.satisfied_by === "cancelled"
                  ? `Satisfied by the OTHER arm: #${e.blocker?.id ?? "?"} was cancelled, and a cancelled blocker is terminal — the claim goes through without it ever integrating.`
                  : e.satisfied_by === "commitless"
                    ? `Satisfied by the COMMITLESS arm: #${e.blocker?.id ?? "?"} is done with no linked commits (nothing to integrate), so the claim goes through on its current status alone.`
                    : iAmBlocker
                      ? `This task is the blocker — #${e.dependent?.id ?? "?"} could not be claimed until it was done AND integrated.`
                      : `This task is the dependent — its own claim was gated on #${e.blocker?.id ?? "?"}.`}
              </span>
              <span className="font-mono type-subtext text-muted-foreground/70">
                blocker status={e.blocker?.status ?? "?"} · integrated_at=
                {e.blocker?.integrated_at ?? "NULL"} · {e.edge_kind} via {e.source ?? "—"}
              </span>
            </li>
          );
        })}
      </ul>

      <div className="flex flex-col gap-1.5">
        {gate.refusal ? (
          <>
            <span className="type-subtext font-medium text-foreground">
              The refusal, verbatim
              <span className="ml-1.5 font-normal text-muted-foreground">
                — reason <code>{gate.refusal.reason}</code>, raised on the → in_progress claim
              </span>
            </span>
            <RawString>{gate.refusal.message}</RawString>
            <RawString>{gate.refusal.predicate}</RawString>
          </>
        ) : (
          // Every edge above is satisfied, so the machine emitted NO refusal on
          // the → in_progress claim — this task was claimable as it stood. An
          // honest empty state, not a counterfactual sentence (the surface must
          // not print a refusal the machine never produced).
          <Hint>
            No unsatisfied dependencies — the → in_progress claim went through
            without a refusal. The machine emits a refusal only when a claim
            would actually be refused, so none is shown here.
          </Hint>
        )}
      </div>

      <Hint>
        The predicate reads <code>integrated_at</code>, the TIMESTAMP — not{" "}
        <code>integration_state</code>. So a blocker stamped{" "}
        <code>none-required</code> (which leaves <code>integrated_at</code> NULL)
        never satisfies a dependent&apos;s claim, no matter how done it looks.
      </Hint>
    </SectionCard>
  );
}

// ── delta receipts ───────────────────────────────────────────────────────────

/**
 * The delta-receipt panel. A delta receipt passes when `head_count <= base_count`
 * — the diff added no NEW violations — which is a different PASS RULE, not a
 * weaker tree-binding. Worth its own panel because it is the only place in the
 * system where a non-zero exit code is still a pass, and a reader who does not
 * know that will file a bug against a working gate.
 */
function DeltaReceipts({ rows }: { rows: ReceiptFixture[] }) {
  const columns: DataTableColumn<ReceiptFixture>[] = [
    {
      key: "command",
      header: "Command",
      render: (r) => (
        <span className="font-mono type-subtext text-foreground">{r.command}</span>
      ),
    },
    {
      key: "counts",
      header: "Base → head",
      width: "w-36",
      render: (r) => (
        <span className="font-mono type-subtext tabular-nums text-foreground">
          {r.base_count} → {r.head_count}
        </span>
      ),
    },
    {
      key: "verdict",
      header: "Verdict",
      width: "w-40",
      render: (r) => {
        const passes = (r.head_count ?? 0) <= (r.base_count ?? 0);
        return (
          <div className="flex flex-col items-start gap-1">
            <IntentBadge intent={passes ? "success" : "danger"}>
              {passes ? "no new violations" : "regressed"}
            </IntentBadge>
            <span className="font-mono type-subtext text-muted-foreground/70">
              exit {r.exit_code}
            </span>
          </div>
        );
      },
    },
    {
      key: "baseline",
      header: "Baseline",
      width: "w-56",
      render: (r) => (
        <div className="flex flex-col gap-1 font-mono type-subtext">
          <span className="text-foreground">
            base_tree {r.base_tree_hash?.slice(0, 12) ?? "—"}
          </span>
          {/* NULL here is absent evidence, not zero. Say which. */}
          <span className="text-muted-foreground">
            base_commit {r.base_commit ?? "NULL — pre-v158"}
          </span>
          <span className="text-muted-foreground">
            delta_count {r.delta_count ?? "NULL — pre-v158"}
          </span>
        </div>
      ),
    },
    {
      key: "recorded",
      header: "Recorded",
      width: "w-40",
      render: (r) => (
        <span className="type-subtext text-muted-foreground">
          {formatDateTime(r.recorded_at)}
        </span>
      ),
    },
  ];

  return (
    <SectionCard
      title="Delta receipts"
      description="Two gates on main carry a non-zero baseline, so their criteria are measured as a delta: the receipt passes when the diff adds no NEW violations. It is the only place a non-zero exit is still a pass."
      aside={<IntentBadge intent="neutral">{rows.length} in the vault</IntentBadge>}
    >
      <DataTable
        rows={rows}
        columns={columns}
        rowKey={(r) => r.id}
        wrapInCard={false}
      />
      <Hint>
        Every delta receipt in the vault has <code>base_commit</code> and{" "}
        <code>delta_count</code> NULL — they predate the migration that added those
        columns. The evaluator&apos;s rule is that this is{" "}
        <strong>absent evidence, not contradiction</strong>, so the rows still bind.
        Rendering NULL as <code>0</code> would silently convert &quot;we did not
        measure&quot; into &quot;we measured zero&quot;.
      </Hint>
    </SectionCard>
  );
}

// ── the tab ──────────────────────────────────────────────────────────────────

/**
 * Two exemplars, switchable, because one task cannot show both shapes: #44115 is
 * the long grind (five refusals, two trees, a refusal after landing) and #44173
 * is the fast catch (three refusals inside six minutes, a park, a diverged tier,
 * green at 07:43:08).
 */
/**
 * Two exemplars were the prototype's first shape. They are now the FALLBACK: the
 * tab reads whichever task `?done=` names, live, on every request — so a refusal
 * recorded thirty seconds ago shows up on refresh. The picker offers recent
 * stamped tasks, but any id in the URL works.
 */
export function DoneTab({
  task,
  readable,
  unreadableReason,
  candidates,
  deltaReceipts,
  stampTotals,
  isFallback,
  slug: _slug,
  onSelectTask,
}: DoneTabProps) {
  // TRI-STATE. "The read failed" and "this task has no loop" are opposite facts
  // and must never render the same way — a page that draws an unreachable vault
  // as an empty loop is the fail-open shape the whole system exists to prevent.
  if (!readable) {
    return (
      <SectionCard
        title="Could not read the vault"
        description="This is not an empty loop — it is a failed read. The difference matters: an empty loop would mean this task has no criteria, which is a claim we cannot currently make."
      >
        <RawString>{unreadableReason ?? "unknown read failure"}</RawString>
      </SectionCard>
    );
  }

  if (!task) {
    // Product path (isFallback always false): do not invent criteria. Show the
    // same "Done when" prose the overview card uses, plus a picker for real tasks.
    return (
      <div className="flex flex-col gap-6">
        <SectionCard
          title="Done"
          description="A run is Done only once a different agent than the shipper has approved and the work is merged to main. This workflow has no live Definition-of-Done sample threaded in — pick a stamped task below to inspect its recursive-done loop, or open a workflow that carries criteria (code-ship-verify)."
          aside={<IntentBadge intent="neutral">no invented criteria</IntentBadge>}
        >
          <p className="type-body text-muted-foreground">
            The gate enforces one thing mechanically — the id that approves cannot be
            the id that claimed — and every derived criterion must carry a receipt
            bound to the tree under review. Nothing on this panel is a fabricated
            demo criterion.
          </p>
          {candidates.length > 0 ? (
            <TaskPicker candidates={candidates} selected={null} onSelectTask={onSelectTask} />
          ) : (
            <p className="type-subtext text-muted-foreground">
              No recently stamped tasks in the vault to offer as a sample.
            </p>
          )}
        </SectionCard>
      </div>
    );
  }

  const refusals = task.loop.filter((n) => n.stage === "refuse").length;
  const bound = task.criteria.filter((c) => c.binds).length;

  return (
    <div className="flex flex-col gap-6">
      <SectionCard
        title="Is this actually done?"
        description="A task is done when a different agent than the one who claimed it says so, every derived criterion carries a receipt bound to the tree under review, and the work is provably an ancestor of main. Read live from the vault on every request."
        aside={
          isFallback ? (
            <IntentBadge intent="warning" withDot>
              frozen fallback
            </IntentBadge>
          ) : (
            <IntentBadge intent="success" withDot>
              live
            </IntentBadge>
          )
        }
        action={<TaskPicker candidates={candidates} selected={task.id} onSelectTask={onSelectTask} />}
      >
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className="font-mono type-body text-muted-foreground">#{task.id}</span>
          <p className="min-w-0 max-w-3xl type-body leading-ui text-foreground">
            {task.title}
          </p>
        </div>
        <MiniStatStrip
          stats={[
            { label: "Shipper", value: task.shipper, sub: "held the claim" },
            { label: "Verifier", value: task.verifier, sub: "gate refuses if these match" },
            {
              label: "Refused",
              value: `${refusals}×`,
              sub: refusals === 0 ? "went green first time" : "before it went green",
            },
            {
              label: "Done",
              value: formatDateTime(task.stamp.completed_at),
              sub: `${bound}/${task.criteria.length} criteria binding`,
            },
          ]}
        />
        {isFallback && (
          <Hint>
            The live read returned nothing renderable for this id, so this is the
            frozen 2026-07-28 snapshot from <code>_done-fixture.ts</code>. Every
            number in it is real, but it is <strong>not</strong> current — pick a
            task above to go back to the live path.
          </Hint>
        )}
      </SectionCard>

      <LoopSpine task={task} />
      <CriteriaPanel task={task} />
      <VerificationTrail task={task} />
      <LandingCard task={task} totals={stampTotals} />
      {task.dependency ? (
        <DependencyGate gate={task.dependency} taskId={task.id} />
      ) : (
        <SectionCard
          title="Dependency gate"
          description="A dependent's claim is refused until its blocker is done AND integrated. This task sits on no dependency edge, so the gate never fired for it."
          aside={<IntentBadge intent="neutral">no edges</IntentBadge>}
        >
          <Hint>
            Most tasks carry no <code>depends_on</code> edge. That is a real
            absence, not a missing panel — the gate exists, it simply had nothing
            to hold back here.
          </Hint>
        </SectionCard>
      )}
      <DeltaReceipts rows={deltaReceipts} />
    </div>
  );
}

/**
 * The picker. Real ids out of the vault, and a link rather than local state so
 * the choice lands in the URL — `?tab=done&done=44173` is shareable, and a
 * refresh re-reads the vault instead of replaying a snapshot in React state.
 */
function TaskPicker({
  candidates,
  selected,
  onSelectTask,
}: {
  candidates: { id: number; title: string }[];
  selected: number | null;
  onSelectTask: (taskId: number) => void;
}) {
  if (candidates.length === 0) return null;
  return (
    <div className="flex max-w-md flex-wrap items-center justify-end gap-1">
      {candidates.slice(0, 8).map((c) => (
        <Button
          key={c.id}
          type="button"
          variant={c.id === selected ? "default" : "ghost"}
          size="sm"
          title={c.title}
          onClick={() => onSelectTask(c.id)}
        >
          #{c.id}
        </Button>
      ))}
    </div>
  );
}
